import type { DomainEventDefinition, JsonValue, RecordDefinition, Transaction } from "@e308/core";
import type { CombatDamageReport } from "./combat-types.js";
import { UNITS } from "./content/buildings.js";
import type { SeatModel } from "./content-types.js";
import { cloneDiplomacy } from "./diplomacy-state.js";
import type { DamageTally, DiplomacyState, MatchLogPayload } from "./diplomacy-types.js";
import { ECONOMY_BUILDINGS, type EconomySettlement } from "./economy-types.js";
import { formatMicro, type Micro, microFromString } from "./exact-numbers.js";
import { buildingResource, unitResource } from "./seat-resources.js";

type LogEvent = DomainEventDefinition<MatchLogPayload & JsonValue>;

export function recordProductionMilestones(
  transaction: Transaction<Micro>,
  model: SeatModel,
  result: EconomySettlement,
  diplomacyDefinition: RecordDefinition<DiplomacyState & JsonValue>,
  logEvent: LogEvent,
): void {
  let state = transaction.getRecord(diplomacyDefinition) as DiplomacyState;
  const produced = [
    ...ECONOMY_BUILDINGS.filter(
      (id) => result.buildings[id] > transaction.get(buildingResource(model.resources, id)),
    ),
    ...UNITS.map((unit) => unit.id).filter(
      (id) => result.units[id] > transaction.get(unitResource(model.resources, id)),
    ),
  ];
  const added = produced.filter(
    (itemId) => !state.milestones.includes(`${model.seat.id}:${itemId}`),
  );
  if (added.length === 0) return;
  state = cloneDiplomacy({
    ...state,
    milestones: [...state.milestones, ...added.map((itemId) => `${model.seat.id}:${itemId}`)],
  });
  transaction.setRecord(diplomacyDefinition, state as DiplomacyState & JsonValue);
  for (const itemId of added)
    transaction.emit(logEvent, {
      kind: "production-milestone",
      seatId: model.seat.id,
      itemId,
    });
}

export function recordDamageSummaries(
  transaction: Transaction<Micro>,
  reports: readonly CombatDamageReport[],
  diplomacyDefinition: RecordDefinition<DiplomacyState & JsonValue>,
  logEvent: LogEvent,
): void {
  const state = transaction.getRecord(diplomacyDefinition) as DiplomacyState;
  let damage = mergeDamage(state.damage, reports);
  let windowEndsAtMs = state.damageWindowEndsAtMs;
  if (transaction.gameTimeMs() >= windowEndsAtMs) {
    if (damage.length > 0)
      transaction.emit(logEvent, {
        kind: "damage-summary",
        windowEndsAtMs,
        entries: damage,
      });
    damage = [];
    windowEndsAtMs = (Math.floor(transaction.gameTimeMs() / 5_000) + 1) * 5_000;
  }
  transaction.setRecord(
    diplomacyDefinition,
    cloneDiplomacy({ ...state, damage, damageWindowEndsAtMs: windowEndsAtMs }) as DiplomacyState &
      JsonValue,
  );
}

function mergeDamage(
  prior: readonly DamageTally[],
  reports: readonly CombatDamageReport[],
): readonly DamageTally[] {
  const entries = new Map(prior.map((entry) => [damageKey(entry), { ...entry }]));
  for (const report of reports) {
    const key = damageKey(report);
    const current = entries.get(key);
    entries.set(key, current ? addReport(current, report) : { ...report });
  }
  return Object.freeze(
    [...entries.values()]
      .sort((left, right) => damageKey(left).localeCompare(damageKey(right)))
      .map((entry) => Object.freeze(entry)),
  );
}

function addReport(left: DamageTally, right: CombatDamageReport): DamageTally {
  const add = (first: string, second: string) =>
    formatMicro((microFromString(first) + microFromString(second)) as Micro);
  return {
    ...left,
    shieldAbsorbed: add(left.shieldAbsorbed, right.shieldAbsorbed),
    troopDamage: add(left.troopDamage, right.troopDamage),
    infrastructureDamage: add(left.infrastructureDamage, right.infrastructureDamage),
    blastDoorsActive: left.blastDoorsActive || right.blastDoorsActive,
  };
}

function damageKey(report: CombatDamageReport): string {
  return `${report.attackerId}:${report.targetId}:${report.effectiveTarget}`;
}
